import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './deviceManagement.css';
import BASE_URL from "../../config";

const DeviceManagement = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connectedCaseSerials, setConnectedCaseSerials] = useState({});
    const [filters, setFilters] = useState({
        id: '',
        serial: '',
        mac: '',
        uid: '',
        batch_id: '',
        batch_name: '',
        device_key: '',
        status: '',
        current_version: '',
        cust_id: '',
        req_count: '',
        details: '',
        custpbx: '',
        wireguard_public_key: '',
        connected_case_serial: '',
    });

    const [activeSearchKey, setActiveSearchKey] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [addDeviceForm, setAddDeviceForm] = useState({
        mac: '',
        batch_id: '',
        status: 'PENDING',
        cust_id: '',
        details: '',
        connected_case_uid: '',
    });
    const [addDeviceLoading, setAddDeviceLoading] = useState(false);
    const [addDeviceError, setAddDeviceError] = useState(null);
    const [addDeviceSuccess, setAddDeviceSuccess] = useState(false);
    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    // Case search state for Add form
    const [caseSearchQuery, setCaseSearchQuery] = useState('');
    const [caseSearchResults, setCaseSearchResults] = useState([]);
    const [showCaseDropdown, setShowCaseDropdown] = useState(false);
    const [caseSearchLoading, setCaseSearchLoading] = useState(false);

    // Edit device state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editDeviceForm, setEditDeviceForm] = useState({
        uid: '',
        mac: '',
        serial: '',
        batch_id: '',
        status: '',
        current_version: '',
        cust_id: '',
        details: '',
        custpbx: '',
        wireguard_public_key: '',
        device_key: '',
        connected_case_uid: '',
        original_connected_case_uid: '', // Track original for comparison
    });
    const [editDeviceLoading, setEditDeviceLoading] = useState(false);
    const [editDeviceError, setEditDeviceError] = useState(null);
    const [editDeviceSuccess, setEditDeviceSuccess] = useState(false);

    // Case search state for Edit form
    const [editCaseSearchQuery, setEditCaseSearchQuery] = useState('');
    const [editCaseSearchResults, setEditCaseSearchResults] = useState([]);
    const [showEditCaseDropdown, setShowEditCaseDropdown] = useState(false);
    const [editCaseSearchLoading, setEditCaseSearchLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('jwt_token');

        // Fetch devices
        fetch(`${BASE_URL}/api/devices/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch devices');
                }
                return response.json();
            })
            .then(async (data) => {
                setDevices(data);

                // Fetch all cases (pocket devices) to get the connected case serials
                try {
                    const casesResponse = await fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                    });

                    if (casesResponse.ok) {
                        const cases = await casesResponse.json();

                        // Create a mapping of connected_device_uid (device UID) to case serial
                        const serialsMap = {};
                        cases.forEach(caseItem => {
                            if (caseItem.connected_device_uid && caseItem.serial) {
                                serialsMap[caseItem.connected_device_uid] = caseItem.serial;
                            }
                        });

                        setConnectedCaseSerials(serialsMap);
                    }
                } catch (err) {
                    console.error('Error fetching case serials:', err);
                }

                setLoading(false);
            })
            .catch((err) => {
                setError('Error fetching devices');
                setLoading(false);
            });

        // Fetch batches
        setBatchesLoading(true);
        fetch(`${BASE_URL}/api/admin/batches/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch batches');
                }
                return response.json();
            })
            .then((data) => {
                setBatches(data);
                setBatchesLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching batches:', err);
                setBatchesLoading(false);
            });
    }, []);

    const handleFilterChange = (e, key) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [key]: e.target.value
        }));
    };

    const toggleSearchInput = (key) => {
        setActiveSearchKey(prevKey => (prevKey === key ? null : key));
    };

    const filteredDevices = devices.filter((device) =>
        Object.entries(filters).every(([key, value]) => {
            if (!value) return true;

            // Special handling for connected_case_serial filter
            if (key === 'connected_case_serial') {
                const connectedCaseSerial = connectedCaseSerials[device.uid] || "";
                return connectedCaseSerial.toString().toLowerCase().includes(value.toLowerCase());
            }

            const fieldValue = device[key] ?? "";
            return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
        })
    );

    const fetchDevices = async () => {
        const token = localStorage.getItem('jwt_token');

        try {
            // Fetch devices
            const devicesResponse = await fetch(`${BASE_URL}/api/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!devicesResponse.ok) {
                throw new Error('Failed to fetch devices');
            }

            const devicesData = await devicesResponse.json();
            setDevices(devicesData);

            // Also re-fetch case serials to update the mapping
            const casesResponse = await fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (casesResponse.ok) {
                const cases = await casesResponse.json();

                // Create a mapping of device UID to case serial
                const serialsMap = {};
                cases.forEach(caseItem => {
                    if (caseItem.connected_device_uid && caseItem.serial) {
                        serialsMap[caseItem.connected_device_uid] = caseItem.serial;
                    }
                });

                setConnectedCaseSerials(serialsMap);
            }

            setLoading(false);
        } catch (err) {
            setError('Error fetching devices');
            setLoading(false);
        }
    };

    const handleAddDeviceChange = (e) => {
        const { name, value } = e.target;
        setAddDeviceForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddDevice = async (e) => {
        e.preventDefault();
        setAddDeviceLoading(true);
        setAddDeviceError(null);
        setAddDeviceSuccess(false);

        const token = localStorage.getItem('jwt_token');

        // Prepare data - send 0 if no cust_id or batch_id provided
        const formattedData = {
            mac: addDeviceForm.mac,
            batch_id: addDeviceForm.batch_id ? parseInt(addDeviceForm.batch_id) : 0,
            status: addDeviceForm.status || 'PENDING',
            cust_id: addDeviceForm.cust_id ? parseInt(addDeviceForm.cust_id) : 0,
            details: addDeviceForm.details || '',
        };

        try {
            const response = await fetch(`${BASE_URL}/api/admin/add-device/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formattedData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add device');
            }

            const data = await response.json();
            // Handle both response formats: { uid: "..." } or { device: { uid: "..." } }
            const createdDeviceUid = data.uid || data.device?.uid;

            // If a case was selected, create a connection using the new API
            if (addDeviceForm.connected_case_uid && createdDeviceUid) {
                try {
                    const connectionResponse = await fetch(`${BASE_URL}/api/pocket/admin/connections/connect/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            device_uid: createdDeviceUid,
                            case_uid: addDeviceForm.connected_case_uid
                        }),
                    });

                    if (!connectionResponse.ok) {
                        const errorData = await connectionResponse.json();
                        throw new Error(errorData.message || 'Failed to create connection');
                    }
                } catch (err) {
                    // Show error to user if connection failed
                    setAddDeviceError(err.message || 'Error creating connection');
                    setAddDeviceLoading(false);
                    return; // Don't show success or close modal
                }
            }

            setAddDeviceSuccess(true);
            setAddDeviceForm({
                mac: '',
                batch_id: '',
                status: 'PENDING',
                cust_id: '',
                details: '',
                connected_case_uid: '',
            });

            // Refresh the devices list
            fetchDevices();

            // Close modal after 2 seconds
            setTimeout(() => {
                setShowAddModal(false);
                setAddDeviceSuccess(false);
            }, 2000);

        } catch (err) {
            setAddDeviceError(err.message || 'Error adding device');
        } finally {
            setAddDeviceLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setAddDeviceForm({
            mac: '',
            batch_id: '',
            status: 'PENDING',
            cust_id: '',
            details: '',
            connected_case_uid: '',
        });
        setAddDeviceError(null);
        setAddDeviceSuccess(false);
        setCaseSearchQuery('');
        setCaseSearchResults([]);
        setShowCaseDropdown(false);
    };

    // Case search functionality for Add form
    const handleCaseSearch = async (query) => {
        setCaseSearchQuery(query);

        if (!query || query.trim().length < 2) {
            setCaseSearchResults([]);
            setShowCaseDropdown(false);
            return;
        }

        setCaseSearchLoading(true);
        setShowCaseDropdown(true);

        const token = localStorage.getItem('jwt_token');

        try {
            const response = await fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to search cases');
            }

            const cases = await response.json();

            // Filter cases by Serial matching the query
            const filtered = cases.filter(caseItem =>
                caseItem.serial && caseItem.serial.toLowerCase().includes(query.toLowerCase())
            ).slice(0, 10); // Limit to 10 results

            setCaseSearchResults(filtered);
        } catch (err) {
            console.error('Case search error:', err);
            setCaseSearchResults([]);
        } finally {
            setCaseSearchLoading(false);
        }
    };

    const handleCaseSelect = (caseItem) => {
        setAddDeviceForm(prev => ({
            ...prev,
            connected_case_uid: caseItem.uid
        }));
        setCaseSearchQuery(caseItem.serial);
        setShowCaseDropdown(false);
    };

    // Edit device handlers
    const handleEditClick = async (device) => {
        // Get the connected case serial and UID from our mapping
        const connectedCaseSerial = connectedCaseSerials[device.uid] || '';

        // Find the case UID from the cases by matching the device UID
        let connectedCaseUid = '';
        try {
            const token = localStorage.getItem('jwt_token');
            const casesResponse = await fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (casesResponse.ok) {
                const cases = await casesResponse.json();
                const connectedCase = cases.find(c => c.connected_device_uid === device.uid);
                if (connectedCase) {
                    connectedCaseUid = connectedCase.uid;
                }
            }
        } catch (err) {
            console.error('Error fetching connected case:', err);
        }

        setEditDeviceForm({
            uid: device.uid,
            mac: device.mac || '',
            serial: device.serial || '',
            batch_id: device.batch_id ?? '',
            status: device.status || 'PENDING',
            current_version: device.current_version ?? '',
            cust_id: device.cust_id ?? '',
            details: device.details || '',
            custpbx: device.custpbx || '',
            wireguard_public_key: device.wireguard_public_key || '',
            device_key: device.device_key || '',
            connected_case_uid: connectedCaseUid, // Store the original connected case UID
            original_connected_case_uid: connectedCaseUid, // Track original for comparison
        });
        setEditCaseSearchQuery(connectedCaseSerial);
        setShowEditModal(true);
    };

    const handleEditDeviceChange = (e) => {
        const { name, value } = e.target;
        setEditDeviceForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateDevice = async (e) => {
        e.preventDefault();
        setEditDeviceLoading(true);
        setEditDeviceError(null);
        setEditDeviceSuccess(false);

        const token = localStorage.getItem('jwt_token');

        // Prepare data for PATCH request
        const formattedData = {
            mac: editDeviceForm.mac,
            serial: editDeviceForm.serial,
            // batch is a ForeignKey - send null if 0 or empty
            batch: editDeviceForm.batch_id && parseInt(editDeviceForm.batch_id) > 0
                ? parseInt(editDeviceForm.batch_id)
                : null,
            status: editDeviceForm.status,
            current_version: editDeviceForm.current_version,
            // cust_id is PositiveIntegerField with default=0 - 0 is valid
            cust_id: editDeviceForm.cust_id !== '' && editDeviceForm.cust_id !== null && editDeviceForm.cust_id !== undefined
                ? parseInt(editDeviceForm.cust_id)
                : 0,
            details: editDeviceForm.details,
            custpbx: editDeviceForm.custpbx,
            wireguard_public_key: editDeviceForm.wireguard_public_key,
            device_key: editDeviceForm.device_key,
        };

        try {
            const response = await fetch(`${BASE_URL}/api/admin/update-device/${editDeviceForm.uid}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formattedData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update device');
            }

            const data = await response.json();

            // Handle connection changes
            const oldCaseUid = editDeviceForm.original_connected_case_uid;
            const newCaseUid = editDeviceForm.connected_case_uid;

            // If connection changed, handle disconnect/reconnect
            if (oldCaseUid !== newCaseUid) {
                try {
                    // First, disconnect old connection if it exists
                    if (oldCaseUid) {
                        const disconnectResponse = await fetch(`${BASE_URL}/api/pocket/admin/connections/disconnect/`, {
                            method: 'DELETE',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                device_uid: editDeviceForm.uid
                            }),
                        });

                        if (!disconnectResponse.ok) {
                            console.warn('Failed to disconnect old connection');
                        }
                    }

                    // Then, create new connection if a case is selected
                    if (newCaseUid) {
                        const connectionResponse = await fetch(`${BASE_URL}/api/pocket/admin/connections/connect/`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                device_uid: editDeviceForm.uid,
                                case_uid: newCaseUid
                            }),
                        });

                        if (!connectionResponse.ok) {
                            const errorData = await connectionResponse.json();
                            throw new Error(errorData.message || 'Failed to create connection');
                        }
                    }
                } catch (err) {
                    // Show error to user if connection failed
                    setEditDeviceError(err.message || 'Error updating connection');
                    setEditDeviceLoading(false);
                    return; // Don't show success or close modal
                }
            }

            setEditDeviceSuccess(true);

            // Refresh the devices list with a small delay to ensure backend has processed
            setTimeout(() => {
                fetchDevices();
            }, 200);

            // Close modal after 2 seconds
            setTimeout(() => {
                setShowEditModal(false);
                setEditDeviceSuccess(false);
            }, 2000);

        } catch (err) {
            setEditDeviceError(err.message || 'Error updating device');
        } finally {
            setEditDeviceLoading(false);
        }
    };

    // Disconnect handler for edit form
    const handleDisconnectCase = () => {
        const confirmDisconnect = window.confirm(
            'Are you sure you want to disconnect this case from the device?\n\nThis will remove the connection but not delete the device or case.'
        );

        if (confirmDisconnect) {
            setEditDeviceForm(prev => ({
                ...prev,
                connected_case_uid: ''
            }));
            setEditCaseSearchQuery('');
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditDeviceForm({
            uid: '',
            mac: '',
            serial: '',
            batch_id: '',
            status: '',
            current_version: '',
            cust_id: '',
            details: '',
            custpbx: '',
            wireguard_public_key: '',
            device_key: '',
            connected_case_uid: '',
            original_connected_case_uid: '',
        });
        setEditDeviceError(null);
        setEditDeviceSuccess(false);
        setEditCaseSearchQuery('');
        setEditCaseSearchResults([]);
        setShowEditCaseDropdown(false);
    };

    // Case search functionality for Edit form
    const handleEditCaseSearch = async (query) => {
        setEditCaseSearchQuery(query);

        if (!query || query.trim().length < 2) {
            setEditCaseSearchResults([]);
            setShowEditCaseDropdown(false);
            return;
        }

        setEditCaseSearchLoading(true);
        setShowEditCaseDropdown(true);

        const token = localStorage.getItem('jwt_token');

        try {
            const response = await fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to search cases');
            }

            const cases = await response.json();

            // Filter cases by Serial matching the query
            const filtered = cases.filter(caseItem =>
                caseItem.serial && caseItem.serial.toLowerCase().includes(query.toLowerCase())
            ).slice(0, 10); // Limit to 10 results

            setEditCaseSearchResults(filtered);
        } catch (err) {
            console.error('Case search error:', err);
            setEditCaseSearchResults([]);
        } finally {
            setEditCaseSearchLoading(false);
        }
    };

    const handleEditCaseSelect = (caseItem) => {
        setEditDeviceForm(prev => ({
            ...prev,
            connected_case_uid: caseItem.uid
        }));
        setEditCaseSearchQuery(caseItem.serial);
        setShowEditCaseDropdown(false);
    };

    // Delete device handler
    const handleDeleteDevice = async (device) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete this device?\n\nUID: ${device.uid}\nSerial: ${device.serial}\nMAC: ${device.mac}\n\nThis action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        const token = localStorage.getItem('jwt_token');

        try {
            const response = await fetch(`${BASE_URL}/api/admin/delete-device/${device.uid}/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete device');
            }

            const data = await response.json();
            alert(`Device deleted successfully!\n\nUID: ${data.deleted_device.uid}\nSerial: ${data.deleted_device.serial}`);

            // Refresh the devices list
            fetchDevices();

        } catch (err) {
            alert(`Error: ${err.message || 'Failed to delete device'}`);
            console.error('Delete device error:', err);
        }
    };

    if (loading) {
        return (
            <div className="device-mgmt-dashboard">
                <div className="device-mgmt-loading">
                    <div className="device-mgmt-spinner"></div>
                    <p>Loading device data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="device-mgmt-dashboard">
                <div className="device-mgmt-error">
                    <div className="error-icon">⚠️</div>
                    <h3>Error</h3>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="device-mgmt-dashboard">
            {/* Add Device Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add New Device</h2>
                            <button className="modal-close" onClick={handleCloseModal}>✕</button>
                        </div>

                        <form onSubmit={handleAddDevice}>
                            <div className="modal-body">
                                {addDeviceError && (
                                    <div className="alert alert-error">
                                        <span className="alert-icon">⚠️</span>
                                        {addDeviceError}
                                    </div>
                                )}

                                {addDeviceSuccess && (
                                    <div className="alert alert-success">
                                        <span className="alert-icon">✓</span>
                                        Device added successfully!
                                    </div>
                                )}

                                <div className="form-section">
                                    <h3 className="form-section-title">Required Information</h3>

                                    <div className="form-group">
                                        <label htmlFor="mac">MAC Address *</label>
                                        <input
                                            type="text"
                                            id="mac"
                                            name="mac"
                                            value={addDeviceForm.mac}
                                            onChange={handleAddDeviceChange}
                                            placeholder="AA:BB:CC:DD:EE:FF"
                                            required
                                            className="form-input"
                                        />
                                        <span className="form-hint">Format: XX:XX:XX:XX:XX:XX</span>
                                    </div>

                                </div>

                                <div className="form-section">
                                    <h3 className="form-section-title">Optional Information</h3>

                                    <div className="form-group">
                                        <label htmlFor="batch_id">Batch</label>
                                        <select
                                            id="batch_id"
                                            name="batch_id"
                                            value={addDeviceForm.batch_id}
                                            onChange={handleAddDeviceChange}
                                            className="form-input"
                                            disabled={batchesLoading}
                                        >
                                            <option value="">-- Select Batch --</option>
                                            {batches.map((batch) => (
                                                <option key={batch.id} value={batch.id}>
                                                    {batch.batch_name}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="form-hint">
                                            {batchesLoading ? 'Loading batches...' : 'Select a batch for this device'}
                                        </span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="status">Status</label>
                                        <select
                                            id="status"
                                            name="status"
                                            value={addDeviceForm.status}
                                            onChange={handleAddDeviceChange}
                                            className="form-input"
                                        >
                                            <option value="PENDING">Pending</option>
                                            <option value="ACTIVE">Active</option>
                                            <option value="INACTIVE">Inactive</option>
                                        </select>
                                        <span className="form-hint">Device operational status</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="cust_id">Customer ID</label>
                                        <input
                                            type="number"
                                            id="cust_id"
                                            name="cust_id"
                                            value={addDeviceForm.cust_id}
                                            onChange={handleAddDeviceChange}
                                            placeholder="Enter customer ID"
                                            className="form-input"
                                        />
                                        <span className="form-hint">Assign device to customer</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="connected_case">Connected Case Serial</label>
                                        <div className="searchable-dropdown-container">
                                            <input
                                                type="text"
                                                id="connected_case"
                                                name="connected_case"
                                                value={caseSearchQuery}
                                                onChange={(e) => handleCaseSearch(e.target.value)}
                                                onFocus={() => {
                                                    if (caseSearchQuery.length >= 2) {
                                                        setShowCaseDropdown(true);
                                                    }
                                                }}
                                                placeholder="Type to search case serial..."
                                                className="form-input"
                                                autoComplete="off"
                                            />
                                            {caseSearchLoading && (
                                                <div className="search-loading">Searching...</div>
                                            )}
                                            {showCaseDropdown && caseSearchResults.length > 0 && (
                                                <div className="search-dropdown">
                                                    {caseSearchResults.map((caseItem) => (
                                                        <div
                                                            key={caseItem.id}
                                                            className="search-dropdown-item"
                                                            onClick={() => handleCaseSelect(caseItem)}
                                                        >
                                                            <div className="device-uid">{caseItem.serial}</div>
                                                            <div className="device-details">
                                                                UID: {caseItem.uid} | MAC: {caseItem.mac}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {showCaseDropdown && !caseSearchLoading && caseSearchResults.length === 0 && caseSearchQuery.length >= 2 && (
                                                <div className="search-dropdown">
                                                    <div className="search-dropdown-empty">No cases found</div>
                                                </div>
                                            )}
                                        </div>
                                        <span className="form-hint">Search by case serial to connect</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="details">Details</label>
                                        <textarea
                                            id="details"
                                            name="details"
                                            value={addDeviceForm.details}
                                            onChange={handleAddDeviceChange}
                                            placeholder="Enter additional details or notes"
                                            rows="3"
                                            className="form-input"
                                        />
                                        <span className="form-hint">Additional notes or information</span>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseModal}
                                    disabled={addDeviceLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={addDeviceLoading}
                                >
                                    {addDeviceLoading ? 'Adding...' : 'Add Device'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Device Modal */}
            {showEditModal && (
                <div className="modal-overlay edit-modal-overlay" onClick={handleCloseEditModal}>
                    <div className="modal-content edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header edit-modal-header">
                            <h2>Edit Device</h2>
                            <button className="modal-close" onClick={handleCloseEditModal}>✕</button>
                        </div>

                        <form onSubmit={handleUpdateDevice}>
                            <div className="modal-body edit-modal-body">
                                {editDeviceError && (
                                    <div className="alert alert-error">
                                        <span className="alert-icon">⚠️</span>
                                        {editDeviceError}
                                    </div>
                                )}

                                {editDeviceSuccess && (
                                    <div className="alert alert-success">
                                        <span className="alert-icon">✓</span>
                                        Device updated successfully!
                                    </div>
                                )}

                                <div className="form-section edit-form-section">
                                    <h3 className="form-section-title">Device Information</h3>

                                    <div className="form-group">
                                        <label htmlFor="edit-uid">UID (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-uid"
                                            value={editDeviceForm.uid}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-serial">Serial Number (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-serial"
                                            value={editDeviceForm.serial}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-cust_id">Customer ID</label>
                                        <input
                                            type="number"
                                            id="edit-cust_id"
                                            name="cust_id"
                                            value={editDeviceForm.cust_id}
                                            onChange={handleEditDeviceChange}
                                            placeholder="Enter customer ID"
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-mac">MAC Address (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-mac"
                                            value={editDeviceForm.mac}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-device_key">Device Key (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-device_key"
                                            value={editDeviceForm.device_key}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-wireguard_public_key">WireGuard Public Key (Read-only)</label>
                                        <textarea
                                            id="edit-wireguard_public_key"
                                            value={editDeviceForm.wireguard_public_key}
                                            rows="3"
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-custpbx">Customer PBX (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-custpbx"
                                            value={editDeviceForm.custpbx}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-batch_id">Batch</label>
                                        <select
                                            id="edit-batch_id"
                                            name="batch_id"
                                            value={editDeviceForm.batch_id}
                                            onChange={handleEditDeviceChange}
                                            className="form-input"
                                            disabled={batchesLoading}
                                        >
                                            <option value="">-- No Batch --</option>
                                            {batches.map((batch) => (
                                                <option key={batch.id} value={batch.id}>
                                                    {batch.batch_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-current_version">Current Version</label>
                                        <input
                                            type="text"
                                            id="edit-current_version"
                                            name="current_version"
                                            value={editDeviceForm.current_version}
                                            onChange={handleEditDeviceChange}
                                            placeholder="Enter version"
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-connected_case">Connected Case Serial</label>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <div className="searchable-dropdown-container" style={{ flex: 1 }}>
                                                <input
                                                    type="text"
                                                    id="edit-connected_case"
                                                    name="edit-connected_case"
                                                    value={editCaseSearchQuery}
                                                    onChange={(e) => handleEditCaseSearch(e.target.value)}
                                                    onFocus={() => {
                                                        if (editCaseSearchQuery.length >= 2) {
                                                            setShowEditCaseDropdown(true);
                                                        }
                                                    }}
                                                    placeholder="Type to search case serial..."
                                                    className="form-input"
                                                    autoComplete="off"
                                                    style={{ margin: 0 }}
                                                />
                                                {editCaseSearchLoading && (
                                                    <div className="search-loading">Searching...</div>
                                                )}
                                                {showEditCaseDropdown && editCaseSearchResults.length > 0 && (
                                                    <div className="search-dropdown">
                                                        {editCaseSearchResults.map((caseItem) => (
                                                            <div
                                                                key={caseItem.id}
                                                                className="search-dropdown-item"
                                                                onClick={() => handleEditCaseSelect(caseItem)}
                                                            >
                                                                <div className="device-uid">{caseItem.serial}</div>
                                                                <div className="device-details">
                                                                    UID: {caseItem.uid} | MAC: {caseItem.mac}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {showEditCaseDropdown && !editCaseSearchLoading && editCaseSearchResults.length === 0 && editCaseSearchQuery.length >= 2 && (
                                                    <div className="search-dropdown">
                                                        <div className="search-dropdown-empty">No cases found</div>
                                                    </div>
                                                )}
                                            </div>
                                            {editCaseSearchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={handleDisconnectCase}
                                                    className="btn-delete"
                                                    style={{
                                                        padding: '10px 20px',
                                                        minHeight: '42px',
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 'fit-content'
                                                    }}
                                                    title="Disconnect case"
                                                >
                                                    Disconnect
                                                </button>
                                            )}
                                        </div>
                                        <span className="form-hint">Search by case serial to connect</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-details">Details</label>
                                        <textarea
                                            id="edit-details"
                                            name="details"
                                            value={editDeviceForm.details}
                                            onChange={handleEditDeviceChange}
                                            placeholder="Enter additional details"
                                            rows="3"
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-status">Status</label>
                                        <select
                                            id="edit-status"
                                            name="status"
                                            value={editDeviceForm.status}
                                            onChange={handleEditDeviceChange}
                                            className="form-input"
                                        >
                                            <option value="PENDING">Pending</option>
                                            <option value="ACTIVE">Active</option>
                                            <option value="INACTIVE">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer edit-modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseEditModal}
                                    disabled={editDeviceLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={editDeviceLoading}
                                >
                                    {editDeviceLoading ? 'Updating...' : 'Update Device'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="device-mgmt-header">
                <div className="header-content">
                    <div className="title-row">
                        <div>
                            <h1 className="device-mgmt-title">Device Management</h1>
                            <p className="device-mgmt-subtitle">View and manage all registered devices</p>
                        </div>
                        <button className="btn-add-device" onClick={() => setShowAddModal(true)}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Device
                        </button>
                    </div>
                </div>
                <div className="header-stats">
                    <div className="stat-card">
                        <div className="stat-label">Total Devices</div>
                        <div className="stat-value total">{devices.length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Active</div>
                        <div className="stat-value active">{devices.filter(d => d.status === 'ACTIVE').length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Pending</div>
                        <div className="stat-value pending">{devices.filter(d => d.status === 'PENDING').length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Inactive</div>
                        <div className="stat-value inactive">{devices.filter(d => d.status === 'INACTIVE').length}</div>
                    </div>
                </div>
            </div>

            <div className="device-mgmt-content">
                <div className="table-container">
                    <div className="table-header">
                        <h2>Devices ({filteredDevices.length})</h2>
                        {Object.entries(filters).some(([_, value]) => value !== '') && (
                            <div className="active-filters-container">
                                {Object.entries(filters)
                                    .filter(([_, value]) => value !== '')
                                    .map(([key, value]) => (
                                        <div key={key} className="active-filter-badge">
                                            <strong>{key.replace('_', ' ')}:</strong> {value}
                                            <button
                                                className="clear-filter"
                                                onClick={() => {
                                                    setFilters(prev => ({...prev, [key]: ''}));
                                                    if (activeSearchKey === key) {
                                                        setActiveSearchKey(null);
                                                    }
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                <button
                                    className="clear-all-filters"
                                    onClick={() => {
                                        setFilters({
                                            id: '',
                                            serial: '',
                                            mac: '',
                                            uid: '',
                                            batch_id: '',
                                            batch_name: '',
                                            device_key: '',
                                            status: '',
                                            current_version: '',
                                            cust_id: '',
                                            req_count: '',
                                            details: '',
                                            custpbx: '',
                                            wireguard_public_key: '',
                                            connected_case_serial: '',
                                        });
                                        setActiveSearchKey(null);
                                    }}
                                >
                                    Clear All
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="table-wrapper">
                        <table className="device-mgmt-table">
                            <thead>
                                <tr>
                                    {[
                                        { key: 'id', label: 'ID' },
                                        { key: 'serial', label: 'Serial' },
                                        { key: 'mac', label: 'MAC Address' },
                                        { key: 'uid', label: 'UID' },
                                        { key: 'connected_case_serial', label: 'Connected Case Serial' },
                                        { key: 'cust_id', label: 'Customer ID' },
                                        { key: 'device_key', label: 'Device Key' },
                                        { key: 'wireguard_public_key', label: 'WireGuard Key' },
                                        { key: 'custpbx', label: 'Customer PBX' },
                                        { key: 'batch_id', label: 'Batch ID' },
                                        { key: 'batch_name', label: 'Batch Name' },
                                        { key: 'current_version', label: 'Version' },
                                        { key: 'details', label: 'Details' },
                                        { key: 'status', label: 'Status' },
                                        { key: 'req_count', label: 'Request Count' },
                                    ].map(({ key, label }) => (
                                        <th key={key}>
                                            <div className="th-content">
                                                <span className="th-label">{label}</span>
                                                <button
                                                    className={`search-toggle ${activeSearchKey === key ? 'active' : ''}`}
                                                    onClick={() => toggleSearchInput(key)}
                                                    title="Search"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="11" cy="11" r="8"></circle>
                                                        <path d="m21 21-4.35-4.35"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                            {activeSearchKey === key && (
                                                <div className="search-input-container">
                                                    <input
                                                        type="text"
                                                        className="search-input"
                                                        placeholder={`Search ${label.toLowerCase()}...`}
                                                        value={filters[key]}
                                                        onChange={(e) => handleFilterChange(e, key)}
                                                        autoFocus
                                                    />
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th>
                                        <div className="th-content">
                                            <span className="th-label">Actions</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDevices.length === 0 ? (
                                    <tr>
                                        <td colSpan="16" className="no-results">
                                            <div className="no-results-content">
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <circle cx="11" cy="11" r="8"></circle>
                                                    <path d="m21 21-4.35-4.35"></path>
                                                </svg>
                                                <p>No devices found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredDevices.map((device) => (
                                        <tr
                                            key={device.id}
                                            className="clickable-row"
                                            onClick={() => handleEditClick(device)}
                                        >
                                            <td className="id-cell">{device.id}</td>
                                            <td className="serial-cell">{device.serial}</td>
                                            <td className="mac-cell">{device.mac}</td>
                                            <td className="uid-cell">{device.uid}</td>
                                            <td className="connected-case-serial-cell">
                                                {connectedCaseSerials[device.uid] || '-'}
                                            </td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                {device.cust_id ? (
                                                    <Link to={`/customer/${device.cust_id}`} className="customer-link">
                                                        {device.cust_id}
                                                    </Link>
                                                ) : (
                                                    <span className="no-customer">-</span>
                                                )}
                                            </td>
                                            <td className="key-cell">{device.device_key}</td>
                                            <td className="wireguard-cell">{device.wireguard_public_key || '-'}</td>
                                            <td className="custpbx-cell">{device.custpbx || '-'}</td>
                                            <td>{device.batch_id || '-'}</td>
                                            <td>{device.batch_name || '-'}</td>
                                            <td>{device.current_version}</td>
                                            <td className="details-cell">{device.details || '-'}</td>
                                            <td>
                                                <span className={`status-badge status-${device.status}`}>
                                                    {device.status}
                                                </span>
                                            </td>
                                            <td>{device.req_count || 0}</td>
                                            <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="btn-edit"
                                                    onClick={() => handleEditClick(device)}
                                                    title="Edit device"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                                <button
                                                    className="btn-delete"
                                                    onClick={() => handleDeleteDevice(device)}
                                                    title="Delete device"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeviceManagement;
